/**
 * Helper utility functions for Barcode and PLU lookups, filtering, and duplicate validation.
 */
export const BarcodeHelper = {
  /**
   * Check if a single item matches a given search/scanned query by:
   * - Name or Item Name (case-insensitive)
   * - PLU (supports 'plu' prefix)
   * - EAN barcode (excluding 'nil', case-insensitive)
   * - Barcode (excluding 'nil', case-insensitive)
   *
   * @param {Object} item - Product item or table row item
   * @param {string|number} query - Scanned code, PLU, or search input
   * @returns {boolean}
   */
  isItemMatch: (item, query) => {
    if (!item || query === undefined || query === null) return false;
    const cleanQuery = String(query).trim().toLowerCase();
    if (!cleanQuery) return false;

    // Match on Name (or item_name)
    const itemName = item.name || item.item_name;
    if (itemName && String(itemName).trim().toLowerCase() === cleanQuery) {
      return true;
    }

    // Match on PLU (directly maps if query starts with 'plu')
    const pluQuery = cleanQuery.startsWith('plu') ? cleanQuery.slice(3).replace(/^[:\s-]+/, '').trim() : cleanQuery;
    if (item.plu && String(item.plu) === pluQuery) {
      return true;
    }

    // Match on EAN
    const itemEan = item.ean && String(item.ean).trim().toLowerCase() !== 'nil' ? String(item.ean).trim().toLowerCase() : '';
    if (itemEan && itemEan === cleanQuery) {
      return true;
    }

    // Match on Barcode
    const itemBarcode = item.barcode && String(item.barcode).trim().toLowerCase() !== 'nil' ? String(item.barcode).trim().toLowerCase() : '';
    if (itemBarcode && itemBarcode === cleanQuery) {
      return true;
    }

    return false;
  },

  /**
   * Find a matching product item by EAN (barcode), PLU, or exact/partial Name.
   */
  findMatchingItem: (query, itemsList) => {
    if (!query || !itemsList || itemsList.length === 0) return null;
    const cleanQuery = String(query).trim().toLowerCase();
    if (!cleanQuery) return null;

    // Directly map to matching PLU product if barcode starts with 'plu'
    if (cleanQuery.startsWith('plu')) {
      const plu = cleanQuery.slice(3).replace(/^[:\s-]+/, '').trim();
      const pluMatch = itemsList.find((i) => i && i.plu && String(i.plu) === plu);
      if (pluMatch) return pluMatch;
    }

    // 1. Exact match on EAN or barcode
    const exactEan = itemsList.find((i) => {
      if (!i) return false;
      const iEan = i.ean && String(i.ean).trim().toLowerCase() !== 'nil' ? String(i.ean).trim().toLowerCase() : '';
      const iBarcode = i.barcode && String(i.barcode).trim().toLowerCase() !== 'nil' ? String(i.barcode).trim().toLowerCase() : '';
      return iEan === cleanQuery || iBarcode === cleanQuery;
    });
    if (exactEan) return exactEan;

    // 2. Exact match on PLU
    const exactPlu = itemsList.find((i) => i && i.plu && String(i.plu) === cleanQuery);
    if (exactPlu) return exactPlu;

    // 3. Exact match on Name
    const exactName = itemsList.find(
      (i) => i && i.name && i.name.trim().toLowerCase() === cleanQuery
    );
    if (exactName) return exactName;

    // 4. Single partial match on Name
    const partialMatches = itemsList.filter(
      (i) => i && i.name && i.name.toLowerCase().includes(cleanQuery)
    );
    if (partialMatches.length === 1) return partialMatches[0];

    return null;
  },

  /**
   * Filter item options for Autocomplete components checking Name, EAN (barcode), and PLU.
   */
  filterItemsByBarcode: (options, inputValue) => {
    if (!inputValue || !inputValue.trim()) return options || [];
    const cleanInput = inputValue.trim().toLowerCase();
    const pluInput = cleanInput.startsWith('plu')
      ? cleanInput.slice(3).replace(/^[:\s-]+/, '').trim()
      : cleanInput;
    return (options || []).filter((option) => {
      if (!option) return false;
      const optEan = option.ean && option.ean !== 'nil' ? String(option.ean).toLowerCase() : '';
      const optBarcode = option.barcode && option.barcode !== 'nil' ? String(option.barcode).toLowerCase() : '';
      const optPlu = option.plu ? String(option.plu) : '';
      const optName = option.name ? option.name.toLowerCase() : '';
      return (
        optName.includes(cleanInput) ||
        optEan.includes(cleanInput) ||
        optBarcode.includes(cleanInput) ||
        (optPlu && (optPlu.includes(cleanInput) || (pluInput && optPlu.includes(pluInput))))
      );
    });
  },

  /**
   * Find duplicate item by EAN barcode (excluding current item ID if editing).
   */
  findDuplicateBarcodeItem: (ean, itemsList, excludeItemId = null) => {
    if (!ean || !String(ean).trim()) return null;
    const cleanCode = String(ean).trim().toLowerCase();
    if (cleanCode === 'nil') return null;
    return (itemsList || []).find((i) => {
      if (!i || (excludeItemId != null && i.id === excludeItemId)) return false;
      const iEan = i.ean && String(i.ean).trim().toLowerCase() !== 'nil' ? String(i.ean).trim().toLowerCase() : '';
      const iBarcode = i.barcode && String(i.barcode).trim().toLowerCase() !== 'nil' ? String(i.barcode).trim().toLowerCase() : '';
      return iEan === cleanCode || iBarcode === cleanCode;
    });
  },
};

export const isItemMatch = BarcodeHelper.isItemMatch;
export const findMatchingItem = BarcodeHelper.findMatchingItem;
export const filterItemsByBarcode = BarcodeHelper.filterItemsByBarcode;
export const findDuplicateBarcodeItem = BarcodeHelper.findDuplicateBarcodeItem;

export default BarcodeHelper;
